import { can } from '@epc/contracts';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import clsx from 'clsx';
import { useEffect, useRef } from 'react';
import { useMe } from '../../lib/session';
import { Page, PageHeader } from '../../ui/layout';

const TABS = [
  { to: '/admin/members', title: 'Сотрудники', perm: 'admin.users' },
  { to: '/admin/delegations', title: 'Замещения', perm: 'admin.users' },
  { to: '/admin/roles', title: 'Роли и права', perm: 'admin.roles' },
  { to: '/admin/org', title: 'Орг-структура', perm: 'admin.org' },
  { to: '/admin/legal-entities', title: 'Юрлица группы', perm: 'admin.org' },
  { to: '/admin/meeting-rooms', title: 'Переговорные', perm: 'admin.org' },
  { to: '/admin/audit', title: 'Журнал изменений', perm: 'admin.audit' },
  { to: '/admin/settings', title: 'Настройки', perm: 'admin.org' },
  { to: '/admin/license', title: 'Лицензия', perm: 'admin.settings' },
  { to: '/admin/branding', title: 'Оформление', perm: 'admin.settings' },
  { to: '/admin/privacy', title: 'Данные людей', perm: 'admin.settings' },
  { to: '/admin/files', title: 'Антивирус', perm: 'admin.settings' },
  { to: '/admin/integrations', title: 'Обмен с системами', perm: 'admin.settings' },
  { to: '/admin/backups', title: 'Копии', perm: 'admin.settings' },
  { to: '/admin/status', title: 'Состояние', perm: 'admin.settings' },
] as const;

export function AdminLayout() {
  const me = useMe();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const tabs = TABS.filter((t) => can(me.access, t.perm));
  const nav = useRef<HTMLElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: прокрутка — при смене раздела, активный ищется в разметке
  useEffect(() => {
    const el = nav.current;
    const active = el?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!el || !active) return;
    // На телефоне открытый раздел виден в строке вкладок; страница по вертикали не сдвигается
    el.scrollLeft = Math.max(0, active.offsetLeft - (el.clientWidth - active.offsetWidth) / 2);
  }, [pathname]);
  return (
    <Page>
      <PageHeader
        title="Администрирование"
        subtitle="Кто работает в портале, с какими правами и что менялось."
      />
      <nav
        ref={nav}
        className="-mt-2 mb-6 flex gap-1 overflow-x-auto border-b border-line"
        aria-label="Разделы администрирования"
      >
        {tabs.map((t) => {
          const active = pathname.startsWith(t.to);
          return (
            <Link
              key={t.to}
              to={t.to}
              aria-current={active ? 'page' : undefined}
              className={clsx(
                'relative -mb-px flex h-11 items-center px-3 text-[14px] whitespace-nowrap md:h-10',
                active ? 'border-b-2 border-ink font-semibold text-ink' : 'text-ink-2 hover:text-ink',
              )}
            >
              {t.title}
            </Link>
          );
        })}
      </nav>
      <Outlet />
    </Page>
  );
}
